import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import useClickOutside from '../hooks/useClickOutside';
import { BoxProps } from '@osmosis/tailwind-ui/components/Box';
import { Input } from '@osmosis/tailwind-ui/components/Input';
import { useSearchContext } from '../context/SearchProvider';
import { InputProps } from '@osmosis/tailwind-ui/components/Input';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import { useEmbedOverlay } from '@osmosis/nextjs-components/context/EmbedProvider';

type AutocompleteInputProps = InputProps & {
  onFocusInput?: (e: FocusEvent<HTMLInputElement>) => void;
  ['aria-label']: string;
};

export type AutocompleteProps<Item> = {
  value: string;
  setValue?: (value: string) => void;
  items?: Item[];
  placeholder?: string;
  exitOnTab?: boolean;
  menuProps?: BoxProps;
  inputProps?: AutocompleteInputProps;
  keyExtractor: (item: Item) => string | number;
  renderItem: (item: Item, hasFocus: boolean, itemIndex: number) => React.ReactNode;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onSelect?: (item: Item) => void;
  icon?: JSX.Element;
  dataTestid?: string;
  readonly?: boolean;
};

/**.
 * Update the scroll to show more items as the user arrows up or down
 *
 * @param {HTMLElement} el
 * @param {number} index
 */
const updateScroll = (el: HTMLElement, index: number): void => {
  const parent = el;
  if (parent?.children) {
    const child = (parent.children[0]?.children?.[index] ||
      parent.children[index]) as HTMLElement;
    if (child) {
      const childOffsetTop = child.offsetTop;
      const parentScrollTop = parent.scrollTop;
      const parentHeight = parent.offsetHeight;
      const childHeight = child.offsetHeight;

      if (childOffsetTop + childHeight > parentHeight + parentScrollTop) {
        const scrollToY = childOffsetTop + childHeight - parentHeight + 4;
        parent.scrollTo(0, scrollToY);
      } else if (childOffsetTop < parentScrollTop) {
        parent.scrollTo(0, childOffsetTop);
      }
    }
  }
};

/**
 * Autocomplete of items.
 */
const Autocomplete = <Item,>(props: AutocompleteProps<Item>): JSX.Element => {
  const {
    value,
    setValue,
    placeholder,
    items = [],
    exitOnTab,
    menuProps = {},
    inputProps = {},
    onChange,
    onSelect,
    keyExtractor,
    renderItem,
    icon,
    dataTestid,
    readonly,
  } = props;
  const { onFocusInput, ...restInputProps } =
    inputProps as AutocompleteInputProps;
  const { isSearchResultsOpen, isSearchFieldFocused } = useSearchContext();

  const menuRef = useRef<HTMLDivElement>(null);

  const [menuVisible, setMenuVisible] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEmbedOverlay({
    id: 'autocomplete',
    isOpen: menuVisible,
    onClose: () => setMenuVisible(false),
  });

  // Handle using arrows to scroll up and down
  useEffect(() => {
    if (menuRef.current) updateScroll(menuRef.current, focusedIndex);
  }, [focusedIndex]);

  useEffect(() => {
    setTimeout(() => {
      if (isSearchResultsOpen && !isSearchFieldFocused) {
        setMenuVisible(false);
      }
    });
  }, [isSearchResultsOpen, isSearchFieldFocused]);

  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => setMenuVisible(false));

  const handleSelect = (item: Item): void => {
    setMenuVisible(false);
    onSelect?.(item);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
    setMenuVisible(true);
    onFocusInput?.(e);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setMenuVisible(true);
    onChange?.(e);
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    let newFocusedIndex = focusedIndex;
    switch (e.key) {
      case 'ArrowUp': // Up
        e.preventDefault();
        if (items.length === 0) break;
        newFocusedIndex =
          focusedIndex <= 0 ? items.length - 1 : focusedIndex - 1;
        break;
      case 'ArrowDown': // Down
        e.preventDefault();
        if (items.length === 0) break;
        newFocusedIndex =
          focusedIndex >= items.length - 1 ? 0 : focusedIndex + 1;
        break;
      case 'Tab': // Tab
        if (exitOnTab) {
          setMenuVisible(false);
        }
        break;
      case 'Enter': // Enter
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < items.length) {
          e.stopPropagation();
          handleSelect(items[focusedIndex]);
        }
        break;
      case 'Escape': //Escape
        e.stopPropagation();
        e.preventDefault();
        if (menuVisible) {
          setMenuVisible(false);
        } else {
          setValue?.('');
        }
        break;
      default:
        break;
    }

    setFocusedIndex(newFocusedIndex);
  };

  useEffect(() => {
    // Reset focused index when we close the menu
    if (focusedIndex > -1 && (!menuVisible || !value)) {
      setFocusedIndex(-1);
    }
  }, [menuVisible, value, focusedIndex]);
  return (
    <div className="relative w-full" ref={containerRef}>
      <Input
        role="combobox"
        aria-controls="search-listbox"
        aria-haspopup="listbox"
        aria-expanded={menuVisible}
        aria-activedescendant={
          focusedIndex >= 0 ? `suggestion-${focusedIndex}` : undefined
        }
        aria-autocomplete="list"
        data-testid={dataTestid && `${dataTestid}-input`}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        {...(restInputProps as React.InputHTMLAttributes<HTMLInputElement>)}
        readOnly={readonly}
      />

      {icon}
      {menuVisible && !!items.length && (
        <div
          ref={menuRef}
          {...menuProps}
          className={`absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-gray-200 ${bg0} shadow-lg`}
        >
          <div
            role="listbox"
            id="search-listbox"
            className="border-t border-gray-300"
          >
            {items.map((item, i) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
              <div
                data-testid={dataTestid && `${dataTestid}-item`}
                onClick={() => handleSelect(item)}
                role="option"
                aria-setsize={items.length}
                aria-posinset={i + 1}
                id={`suggestion-${i}`}
                aria-selected={focusedIndex === i ? true : false}
                key={keyExtractor(item)}
              >
                {renderItem(item, i === focusedIndex, i)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
